import { useCallback, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormatListBulletedOutlined, Person } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { FormPageMode } from '@practium/ui';
import { TestimonialForm } from '../components/Forms/TestimonialForm/TestimonialForm';
import type { TestimonialFormRef } from '../components/Forms/TestimonialForm/TestimonialForm';
import { TestimonialPreviewCard } from '../components/Preview/TestimonialPreviewCard/TestimonialPreviewCard';
import { AvatarCard } from '@practium/features/users';
import { usePermissions } from '@practium/features/permissions';
import type { TestimonialFormData } from '../types/landing.types';
import { MOCK_TESTIMONIALS, LANDING_ROUTES } from '../constants/landing.constants';
import { deleteTestimonial } from '../services/testimonial.service';
import styles from './AddTestimonialPage.module.css';

export const ViewTestimonialPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { abilities } = usePermissions();
	const canEdit = abilities.can('update', 'testimonials');
	const canDelete = abilities.can('delete', 'testimonials');
	const id = searchParams.get('id') ?? '';

	const mock = MOCK_TESTIMONIALS.find(t => t._id === id);
	const avatarUrl = mock?.avatarUrl;

	const initialData: TestimonialFormData = {
		name: mock?.name ?? '',
		occupation: mock?.occupation ?? '',
		testimonial: mock?.testimonial ?? '',
		status: mock?.status === 'active' ? 'yes' : 'no',
	};

	const [currentMode, setCurrentMode] = useState<FormPageMode>('view');
	const [isLoading, setIsLoading] = useState(false);
	const [avatarFile, setAvatarFile] = useState<File | null>(null);
	const [liveForm, setLiveForm] = useState<TestimonialFormData>(initialData);

	const formRef = useRef<TestimonialFormRef>(null);

	const { success: toastSuccess, error: toastError } = useToast();

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const handleSubmit = useCallback(() => {
		navigate(LANDING_ROUTES.CONTROL);
	}, [navigate]);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			setCurrentMode('view');
			setLiveForm(initialData);
			setAvatarFile(null);
		};
		if (!avatarFile && !formRef.current?.isDirty?.()) {
			revert();
			return;
		}
		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (!confirmed) return;
		revert();
	}, [avatarFile, confirm, initialData]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir depoimento',
			message: 'Tem certeza que deseja excluir este depoimento? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (!confirmed) return;
		try {
			await deleteTestimonial(id);
			toastSuccess('Depoimento excluído com sucesso');
			navigate(LANDING_ROUTES.CONTROL);
		} catch {
			toastError('Erro ao excluir depoimento');
		}
	}, [confirm, id, navigate, toastSuccess, toastError]);

	const handleEdit = useCallback(() => setCurrentMode('edit'), []);
	const handleSave = useCallback(() => formRef.current?.submit(), []);

	const isView = currentMode === 'view';

	const pageTitle =
		currentMode === 'view' ? 'Informações do Depoimento' : 'Editar Depoimento';
	const pageSubtitle =
		currentMode === 'view'
			? 'Visualize os dados do depoimento'
			: 'Edite os dados do depoimento';

	return (
		<div className={styles.page}>
			<PageHeader
				title={pageTitle}
				subtitle={pageSubtitle}
				onBack={() => navigate(LANDING_ROUTES.CONTROL)}
			/>
			<div className={styles.cards}>
				<div className={styles.mainContent}>
					<FormCard
						icon={<FormatListBulletedOutlined />}
						title="Informações Gerais"
						className={styles.mainCard}
					>
						<TestimonialForm
							ref={formRef}
							mode={currentMode}
							initialData={initialData}
							testimonialId={id}
							avatarFile={avatarFile}
							onSubmit={handleSubmit}
							onLoadingChange={setIsLoading}
							onFormChange={setLiveForm}
						/>
					</FormCard>
					<div className={styles.pageActions}>
						<FormActions
							mode={currentMode}
							onCancel={handleCancel}
							onSave={handleSave}
							isLoading={isLoading}
							onDelete={canDelete ? handleDelete : undefined}
							onEdit={canEdit ? handleEdit : undefined}
						/>
					</div>
				</div>
				{isView ? (
					<div className={styles.sideCard}>
						<FormCard icon={<Person />} title="Preview">
							<div className={styles.formPreviewCard}>
								<TestimonialPreviewCard
									name={liveForm.name}
									occupation={liveForm.occupation}
									testimonial={liveForm.testimonial}
									avatarUrl={avatarUrl}
								/>
							</div>
						</FormCard>
					</div>
				) : (
					<div className={styles.editSide}>
						<AvatarCard
							mode={currentMode}
							file={avatarFile}
							onFileChange={setAvatarFile}
							personName={liveForm.name || undefined}
							avatarUrl={avatarUrl}
							onEditRequest={canEdit ? handleEdit : undefined}
						/>
					</div>
				)}
			</div>
			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
